import loggerStyle from "../../../../commandLogger.module.css";
import emptyStyle from "../../../../../../../Common/emptyState.module.css";

import { useState, useEffect, useRef, useContext, useCallback } from "react";

import { CommandBlock } from "./components/CommandBlock/CommandBlock.tsx";
import { apiFuzzyIOBlocksByPentest } from "../../../../../../../../sdk.ts";

import InfoHover from "../../../../../../../Common/Tooltips/InformationHover/InformationHover.tsx";

import { SquareTerminal, Search, X, RefreshCcw, Ellipsis, ArrowUpToLine, Key, CircleX, Minus } from "lucide-react";
import { AuthContext } from "../../../../../../../../contexts/AuthContext.ts";
import { PentestContext } from "../../../../../../../../contexts/PentestContext.ts";
import { LoggerContext } from "../../../../../../../../contexts/LoggerContext.ts";
import Tooltip from "../../../../../../../Common/Tooltips/Tooltip/Tooltip.tsx";

import InfiniteScroll from "react-infinite-scroll-component";
import { TutorialContext } from "../../../../../../../../contexts/TutorialContext.ts";
import { IOBlock } from "../../../../../../../../sdkStructs.ts";
import { useDebounce } from "use-debounce";

interface Props {
  IOList: IOBlock[];
  hasMore: boolean;
  refresh: (automatic: boolean) => void;
  loadMoreFunction: () => void;

  included: boolean[];
  setIncluded: (value: boolean[]) => void;

  refreshing: boolean;

  selectBlock: (value: IOBlock) => void;
}

const REFRESH_TIME = 5000; // Refresh every 5 seconds

function CommandList({ IOList, refresh, loadMoreFunction, selectBlock, hasMore, refreshing }: Readonly<Props>) {
  const { token } = useContext(AuthContext);
  const { pentestId } = useContext(PentestContext);
  const { tutorialRefs } = useContext(TutorialContext);

  const topRefresh = useRef(null);

  const [automaticRefresh, setAutomaticRefresh] = useState(false);

  // filter states
  const [fuzzySearch, setFuzzySearch] = useState(false);
  const [filteredList, setFilteredList] = useState<IOBlock[]>([]);
  const [filterText, setFilterText] = useState("");
  const [ debouncedFilterText ] = useDebounce(filterText, 500);

  const [spinRefresh, setSpinRefresh] = useState(false);

  const [extendedList, setExtendedList] = useState(false);

  const [renderCount, setRenderCount] = useState(0);
  const forceRerender = () => {
    setRenderCount(renderCount + 1);
  };

  const {
    flagMode,
    toggleFlagMode,
    highlightCommands,
    setHighlightCommands,
    highlightRequests,
    setHighlightRequests,
    tokenDisplay,
    setTokenDisplay,
  } = useContext(LoggerContext);

  const listRef = useRef(null);
  const searchField = useRef<HTMLInputElement>(null);

  const scrollToTop = async () => {
    const scrollableDiv = document.getElementById("scrollableDiv");
    if (!scrollableDiv) return;
    scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });

    const checkScroll = (resolveScroll: (value: string) => void) => {
      if (scrollableDiv.scrollTop === 0) resolveScroll("yay");
      else setTimeout(() => checkScroll(resolveScroll), 100);
    };

    return new Promise(checkScroll);
  };

  // UseEffect for automatic scrolling
  useEffect(() => {
    if (topRefresh.current != null) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) setAutomaticRefresh(true);
        else setAutomaticRefresh(false);
      });
      observer.observe(topRefresh.current);
    }
  }, [loadMoreFunction]);

  useEffect(() => {
    if (fuzzySearch) {
      searchField.current?.focus();
    }
  }, [fuzzySearch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (automaticRefresh) {
        if (!extendedList) {
          refresh(true);
          console.log("Refreshing Automatically");
        }
      }
    }, REFRESH_TIME);

    return () => clearInterval(interval);
  }, [automaticRefresh, refresh, extendedList]);

  function selectCommand(data: IOBlock) {
    if (!flagMode) toggleFlagMode(true);
    selectBlock(data);

    let aux;
    if (data.type === "command") {
      // Procesam selectarea unei comenzi
      aux = highlightCommands;
      aux[data.id] = !aux[data.id];
      setHighlightCommands(aux);
    } else {
      // Procesam selectarea unui request
      aux = highlightRequests;
      aux[data.id] = !aux[data.id];
      setHighlightRequests(aux);
    }
    forceRerender();
  }

  const applyFuzzySearch = useCallback(async () => {
    const newFilteredList = await apiFuzzyIOBlocksByPentest(token, pentestId, debouncedFilterText);
    setFilteredList(newFilteredList.data);
  }, [debouncedFilterText, pentestId, token]);

  useEffect(() => {
    applyFuzzySearch();
  }, [applyFuzzySearch]);

  function displayLoggedCommands() {
    if (!IOList || IOList.length === 0)
      return (
        <div className={emptyStyle.emptyLogger}>
          <SquareTerminal className={emptyStyle.emptyStateImage} stroke="var(--onedark-text)" strokeWidth="1.5" />
          <p className={emptyStyle.emptyTitle}>You haven't logged any commands!</p>
          <p className={emptyStyle.emptyText}>
            1. Download the cli{" "}
            <a className={emptyStyle.link} href="/downloads">
              using this tutorial.
            </a>
            <br />
            2. Run 'pentra' with the above token
            <br />
            3. Start writing commands in another terminal (and refresh)!
          </p>
        </div>
      );

    const displayList: IOBlock[] = fuzzySearch ? filteredList : [...IOList];
    if (!displayList) return;

    return (
      <div id="scrollableDiv" className={loggerStyle.normalList}>
        <div ref={topRefresh} className={loggerStyle.automaticCheckingText}></div>
        <InfiniteScroll
          dataLength={displayList.length}
          next={loadMoreFunction}
          hasMore={hasMore && !fuzzySearch}
          loader={
            <div className={loggerStyle.plusWrapper}>
              <Ellipsis
                onClick={loadMoreFunction}
                className={loggerStyle.plusImage}
                stroke="var(--onedark-text)"
                strokeWidth="2"
              />
            </div>
          }
          endMessage={!fuzzySearch && <h5 className={loggerStyle.endMessage}>This is the start of your pentest</h5>}
          scrollableTarget="scrollableDiv"
          className={loggerStyle.infiniteScroll}
        >
          {displayList.map((element) => {
            const isHighlighted =
              element.type === "command" ? highlightCommands[element.id] : highlightRequests[element.id];
            return (
              <CommandBlock
                key={element.type + element.id}
                standalone={true}
                data={element}
                selectFun={selectCommand}
                selected={isHighlighted}
                reportMode={flagMode}
              />
            );
          })}
        </InfiniteScroll>
      </div>
    );
  }

  function getSearchBox() {
    if (!fuzzySearch) return;
    return (
      <div className={loggerStyle.searchBox}>
        <input
          ref={searchField}
          type="text"
          className={loggerStyle.searchField}
          value={filterText}
          onChange={(event: React.FormEvent<HTMLInputElement>) => {
            setFilterText(event.currentTarget.value);
          }}
        />

        <button className={loggerStyle.fuzzyCloseButton} onClick={() => setFuzzySearch(false)}>
          <X className={loggerStyle.filterImage} stroke="var(--light-blue-icon)" strokeWidth="1.5" />
        </button>
      </div>
    );
  }

  function getFlagButton() {
    if (flagMode)
      return (
        <div className={loggerStyle.lastFilterButton}>
          <Tooltip
            trigger={
              <button className={loggerStyle.filterButton} onClick={() => toggleFlagMode(false)}>
                <CircleX className={loggerStyle.filterImage} color="white" strokeWidth="1.5" />
              </button>
            }
            description="Stop Tagging"
            showArrow
          />
        </div>
      );
    return <></>;
  }

  return (
    <div className={loggerStyle.listWrapper + " " + loggerStyle.commandListWrapper}>
      <div className={loggerStyle.listOptions}>
        <h3>Commands</h3>
        <InfoHover description="Commands that the logger picks up show up here. Tag them based on the vulnerability they reveal." />

        <div className={loggerStyle.firstFilterButton}>
          <Tooltip
            trigger={
              <button
                className={loggerStyle.filterButton}
                ref={tutorialRefs.searchListRef}
                onClick={() => {
                  setFuzzySearch(!fuzzySearch);
                }}
              >
                <Search className={loggerStyle.filterImage} stroke="var(--light-blue-icon)" strokeWidth="1.5" />
              </button>
            }
            description="Search"
            showArrow
          />
        </div>

        <Tooltip
          trigger={
            <button
              ref={tutorialRefs.toggleTokenRef}
              className={loggerStyle.filterButton}
              onClick={() => setTokenDisplay(!tokenDisplay)}
            >
              <Key className={loggerStyle.filterImage} stroke="var(--light-blue-icon)" strokeWidth="1.5" />
              {tokenDisplay && (
                <Minus className={loggerStyle.overlapX} stroke="var(--light-blue-icon)" strokeWidth="1.5" />
              )}
            </button>
          }
          description="Toggle Token"
          showArrow
        />

        {getFlagButton()}

        <div className={loggerStyle.refreshButton}>
          <Tooltip
            trigger={
              <button
                ref={tutorialRefs.refreshListRef}
                className={loggerStyle.filterButton}
                onClick={async () => {
                  setSpinRefresh(true);
                  setTimeout(() => setSpinRefresh(false), 1100);
                  await scrollToTop();
                  refresh(false);
                  setExtendedList(false);
                }}
              >
                {!IOList || IOList.length <= 20 ? (
                  <RefreshCcw
                    stroke="var(--light-blue-icon)"
                    strokeWidth="1.5"
                    className={
                      spinRefresh ? loggerStyle.filterImage + " " + loggerStyle.rotateImage : loggerStyle.filterImage
                    }
                  />
                ) : (
                  <ArrowUpToLine
                    stroke="var(--light-blue-icon)"
                    strokeWidth="1.5"
                    className={
                      spinRefresh ? loggerStyle.filterImage + " " + loggerStyle.rotateImage : loggerStyle.filterImage
                    }
                  />
                )}
              </button>
            }
            description="Refresh"
            showArrow
          />
        </div>
      </div>
      {getSearchBox()}
      <div ref={listRef} className={loggerStyle.listElements + " " + loggerStyle.listCommands}>
        {refreshing ? <h5 className={loggerStyle.endMessage}>Refreshing</h5> : displayLoggedCommands()}
      </div>
    </div>
  );
}
export default CommandList;
